import {
  AttachFileOutlined as AttachFileIcon,
  Delete as DeleteIcon,
  UploadFileOutlined as UploadFileIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { UseFormHandleSubmit } from 'react-hook-form';

import {
  FILE_TYPES,
  MAX_FILE_NUMBER,
  MAX_FILE_SIZE_BYTES,
  UPLOADER_MIME_SETTINGS,
} from 'common/src/file-storage/constants';

import useAuth from 'portal/src/auth/useAuth';
import ConfirmDialog from 'portal/src/components/common/ConfirmDialog';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

const FileUploader = <TFormData = any,>({
  fileTypeCategory,
  refetchKey,
  uploadHandlers: { uploadFiles, handleSubmit },
  isDisabled,
  sidePanel,
  promptMessage,
}: {
  fileTypeCategory: keyof typeof FILE_TYPES;
  uploadHandlers: {
    uploadFiles: (files: File[], data?: TFormData) => Promise<unknown>;
    handleSubmit?: UseFormHandleSubmit<TFormData>;
  };
  isDisabled?: boolean;
  refetchKey?: 'importFiles' | 'statements';
  sidePanel?: ReactNode;
  promptMessage?: string;
}) => {
  const fileTypeList = FILE_TYPES[fileTypeCategory].join(', ').toUpperCase();
  const snackbarHandler = useSnackbarHandler();
  const { hasReadOnlyAccess } = useAuth();
  const [files, setFiles] = useState<File[]>([]);
  const [duplicatedDialog, setDuplicatedDialog] = useState(false);
  const client = useQueryClient();

  const { mutate: handleFileUpload, isPending } = useMutation({
    mutationFn: async ({ files, data }: { files: File[]; data?: TFormData }) =>
      await uploadFiles(files, data),
    onSuccess: () => {
      setFiles([]);
      if (refetchKey) {
        client.invalidateQueries({ queryKey: [refetchKey] });
      }
      snackbarHandler.success(MESSAGES.UPLOAD_FILES.SUCCESS);
    },
    onError: () => {
      snackbarHandler.error(MESSAGES.UPLOAD_FILES.ERROR);
    },
  });

  const disabledAction = useMemo(() => {
    return hasReadOnlyAccess() || isPending || isDisabled;
  }, [hasReadOnlyAccess, isPending, isDisabled]);

  const { getRootProps, getInputProps } = useDropzone({
    disabled: disabledAction,
    onDrop: (acceptedFiles) => {
      const newFiles = acceptedFiles.filter(
        (file) =>
          !files.some((existingFile) => existingFile.name === file.name),
      );

      if (newFiles.length < acceptedFiles.length) setDuplicatedDialog(true);

      setFiles([...files, ...newFiles]);
    },
    onDropRejected: (rejections) => {
      if (rejections && rejections.length > 0) {
        if (
          rejections[0].errors &&
          rejections[0].errors.length > 0 &&
          rejections[0].errors[0].code === 'too-many-files'
        ) {
          snackbarHandler.error(
            `Too many files. Maximum allowed is ${MAX_FILE_NUMBER}`,
          );
          return;
        }

        rejections.forEach((rejection) => {
          rejection.errors.forEach((error) => {
            let message = `File '${rejection.file.name}' was rejected`;

            if (error.code === 'file-too-large') {
              message +=
                ' File size exceeds the maximum allowed size of ~100MB';
            } else if (error.code === 'file-invalid-type') {
              message += ` Invalid file type. Accepted types are: ${fileTypeList}`;
            }

            snackbarHandler.error(message);
          });
        });
      }
    },
    accept: UPLOADER_MIME_SETTINGS[fileTypeCategory],
    maxSize: MAX_FILE_SIZE_BYTES,
    maxFiles: MAX_FILE_NUMBER,
    noKeyboard: true,
  });

  const handleRemoveFile = (file: File) => {
    setFiles(files.filter((existingFile) => existingFile.name !== file.name));
  };

  const closeDuplicatedWarning = () => setDuplicatedDialog(false);

  return (
    <Stack direction="column" spacing={2} sx={{ width: '100%' }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Stack
          direction="row"
          spacing={3}
          sx={{
            width: '100%',
            justifyContent: 'space-between',
            border: 2,
            borderColor: grey[300],
            borderRadius: 1,
            borderStyle: 'dashed',
            cursor: disabledAction ? 'not-allowed' : 'pointer',
            transition: disabledAction ? 'none' : 'all 0.2s ease-in-out',
            ...(disabledAction
              ? {}
              : {
                  '&:hover': {
                    borderColor: 'grey.500',
                    backgroundColor: 'grey.100',
                  },
                }),
          }}
          {...getRootProps({ className: 'dropzone' })}
        >
          <Stack
            direction="column"
            spacing={1}
            sx={{
              p: 2,
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              backgroundColor: 'background.paper',
            }}
          >
            <UploadFileIcon sx={{ fontSize: 40 }} />
            <Typography variant="h6">Choose or drag/drop files</Typography>
            <Typography
              variant="body2"
              sx={{ color: 'grey.500' }}
              gutterBottom={false}
            >
              Accepted file types: {fileTypeList}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'grey.500' }}
              gutterBottom={false}
            >
              Max number of files: {MAX_FILE_NUMBER} (total size ~100 MB)
            </Typography>
            {promptMessage && (
              <Typography variant="body2">{promptMessage}</Typography>
            )}
          </Stack>
          <input {...getInputProps()} />
        </Stack>
        {sidePanel}
      </Stack>
      {files.length > 0 && (
        <Box sx={{ position: 'relative' }}>
          <Paper variant="outlined" elevation={0} sx={{ p: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1">
                Ready to upload the following{' '}
                {files.length === 1 ? 'file' : `${files.length} files`}:
              </Typography>
            </Box>
            <Stack direction="column" spacing={2}>
              {files.map((file) => (
                <Paper key={file.name} elevation={2} sx={{ p: 2 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <AttachFileIcon sx={{ fontSize: 20 }} />
                    <Stack direction="column" spacing={1} sx={{ flexGrow: 1 }}>
                      <Typography variant="body2">{file.name}</Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: 'grey.500' }}
                        gutterBottom={false}
                      >
                        {file.size / 1024} KB
                      </Typography>
                    </Stack>
                    <IconButton
                      aria-label="delete"
                      disabled={disabledAction}
                      onClick={() => handleRemoveFile(file)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </Paper>
              ))}
              <Grid sx={{ display: 'flex', justifyContent: 'end' }}>
                <Button
                  disableElevation
                  disabled={files.length === 0 || disabledAction}
                  color="secondary"
                  endIcon={<UploadFileIcon />}
                  variant="contained"
                  onClick={() => {
                    handleSubmit
                      ? handleSubmit((data) =>
                          handleFileUpload({ files, data }),
                        )()
                      : handleFileUpload({ files });
                  }}
                >
                  Upload Files
                </Button>
              </Grid>
            </Stack>
          </Paper>
          {isPending && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                bgcolor: 'rgba(255, 255, 255, 0.7)',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: (theme) => theme.zIndex.drawer + 1,
              }}
            >
              <CircularProgress color="primary" />
            </Box>
          )}
        </Box>
      )}
      <ConfirmDialog
        open={duplicatedDialog}
        onClose={closeDuplicatedWarning}
        message="Warning: Some files are already in the list and will not be added."
        primaryButtonLabel="Accept"
        onPrimaryButtonClicked={closeDuplicatedWarning}
        renderElement={<WarningIcon sx={{ color: 'warning.main' }} />}
      />
    </Stack>
  );
};

export default FileUploader;
